import React, { useMemo } from 'react';
import { Search, ShoppingCart, Check, Image as ImageIcon } from 'lucide-react';
import { API_BASE_URL } from '../../../../services/api';

export const EstimationCatalog = ({ state, actions }) => {
  const {
    catalogType, setCatalogType, catalogFiles, loadingCatalog, selectedPreviewDoc, setSelectedPreviewDoc,
    previewUrl, selectedMainTypes, setSelectedMainTypes, selectedQualities, setSelectedQualities, 
    selectedSubFields, setSelectedSubFields,
    storeFilter, setStoreFilter, storeStock, loadingStore, storeSearchQuery, setStoreSearchQuery, 
    stockQuantities, setStockQuantities, isLocked
  } = state;
  
  const { handleAddStockItem } = actions;

  // 1. Filter by fixing type (Ready to Fix / Loose Items)
  const baseFilteredStock = useMemo(() => {
    if (!storeStock) return [];
    return storeStock.filter(s => {
      const isReadyToFix = s.item?.fixingType === 'Ready to Fix';
      if (isReadyToFix && !storeFilter.readyToFix) return false;
      if (!isReadyToFix && !storeFilter.looseItems) return false;
      return true;
    });
  }, [storeStock, storeFilter]);

  // 2. Compute Available Main Types
  const availableMainTypes = useMemo(() => {
    return [...new Set(baseFilteredStock.map(s => s.item?.category?.categoryName).filter(Boolean))].sort();
  }, [baseFilteredStock]);

  // 3. Helper to toggle selected main types
  const toggleMainType = (type) => {
    setSelectedMainTypes(prev => {
      if (prev.includes(type)) return prev.filter(t => t !== type);
      return [...prev, type];
    });
  };

  // 4. Helper to toggle quality per category
  const toggleQuality = (category, quality) => {
    setSelectedQualities(prev => {
      const catQualities = prev[category] || [];
      const isSelected = catQualities.includes(quality);
      
      return {
        ...prev,
        [category]: isSelected 
          ? catQualities.filter(q => q !== quality)
          : [...catQualities, quality]
      };
    });
  };

  // 5. Helper to toggle subFields per category
  const toggleSubField = (category, subField) => {
    setSelectedSubFields(prev => {
      const catFields = prev[category] || [];
      const isSelected = catFields.includes(subField);
      
      return {
        ...prev,
        [category]: isSelected 
          ? catFields.filter(f => f !== subField)
          : [...catFields, subField]
      };
    });
  };

  // 6. Build dynamic structure for the selected main types
  const catalogStructure = useMemo(() => {
    if (!selectedMainTypes || selectedMainTypes.length === 0) return [];

    let currentStock = baseFilteredStock.filter(s => selectedMainTypes.includes(s.item?.category?.categoryName));

    if (storeSearchQuery) {
      const q = storeSearchQuery.toLowerCase();
      currentStock = currentStock.filter(s => (s.item?.itemName || '').toLowerCase().includes(q));
    }

    const structure = [];

    // Process each selected main type to maintain order
    selectedMainTypes.forEach(categoryName => {
      const categoryStock = currentStock.filter(s => s.item?.category?.categoryName === categoryName);
      if (categoryStock.length === 0) return;

      // Determine available qualities for this category
      const qualities = [...new Set(categoryStock.map(s => s.item?.quality || 'Standard').filter(Boolean))].sort();
      
      // Get selected qualities for this category, defaulting to all if none selected
      const selectedCatsQualities = selectedQualities[categoryName] || [];
      const activeQualities = selectedCatsQualities.length > 0 ? selectedCatsQualities : qualities;

      // Filter category stock by active qualities
      const activeCategoryStock = categoryStock.filter(s => activeQualities.includes(s.item?.quality || 'Standard'));

      // Determine subField type and available subField values for the ACTIVE qualities
      let subFieldLabel = "Sub Type";
      let subFieldKey = "";
      
      const hasThickness = activeCategoryStock.some(s => s.item?.thickness);
      const hasProfile = activeCategoryStock.some(s => s.item?.profile);
      
      if (hasThickness) {
          subFieldKey = "thickness";
          subFieldLabel = "Thickness";
      } else if (hasProfile) {
          subFieldKey = "profile";
          subFieldLabel = "Profile";
      }

      const availableSubFields = [];
      if (subFieldKey) {
          const uniqueVals = [...new Set(activeCategoryStock.map(s => s.item?.[subFieldKey]).filter(Boolean))].sort();
          availableSubFields.push(...uniqueVals);
      }
      
      const selectedCatsSubFields = selectedSubFields[categoryName] || [];
      const activeSubFields = selectedCatsSubFields.length > 0 ? selectedCatsSubFields : availableSubFields;

      // Filter by subfield if applicable
      let finalStock = activeCategoryStock;
      if (subFieldKey && availableSubFields.length > 0) {
          finalStock = activeCategoryStock.filter(s => {
              const val = s.item?.[subFieldKey];
              return !val || activeSubFields.includes(val);
          });
      }

      // Now group the final stock dynamically
      const groupedProducts = {};

      finalStock.forEach(stockItem => {
        const item = stockItem.item || {};
        
        // Dynamic Grouping Logic matching client workflow
        let groupName = "General Products";
        let subField = "";

        if (item.thickness) {
            subField = item.thickness;
        } else if (item.profile) {
            subField = item.profile;
        }

        const qualityPrefix = item.quality && item.quality !== 'Standard' ? item.quality : '';
        const capCategory = categoryName.charAt(0).toUpperCase() + categoryName.slice(1);
        
        // E.g. "Windows 4/2.5 frames Products" or "Premium Doors Products"
        if (subField) {
            groupName = `${capCategory} ${subField} Products`;
        } else if (qualityPrefix) {
            groupName = `${qualityPrefix} ${capCategory} Products`;
        } else {
            groupName = `${capCategory} Products`;
        }

        if (!groupedProducts[groupName]) {
            groupedProducts[groupName] = [];
        }
        groupedProducts[groupName].push(stockItem);
      });

      // Sort groups alphabetically
      const sortedGroups = Object.keys(groupedProducts).sort().map(groupName => ({
          name: groupName,
          items: groupedProducts[groupName]
      }));

      structure.push({
          categoryName,
          availableQualities: qualities,
          selectedQualities: selectedCatsQualities,
          subFieldLabel,
          availableSubFields,
          selectedSubFields: selectedCatsSubFields,
          groups: sortedGroups
      });

    });

    return structure;
  }, [baseFilteredStock, selectedMainTypes, selectedQualities, selectedSubFields, storeSearchQuery]);


  // Helper for rendering image
  const getImageUrl = (item) => {
    if (!item?.image) return null;
    return item.image.startsWith('http') ? item.image : `${API_BASE_URL}/api/inventory/settings/images/${item.image}`;
  };

  return (
    <div className="flex flex-col h-full bg-slate-50 border-r border-slate-200">
      
      {/* Top Header Controls */}
      <div className="bg-white p-5 border-b border-slate-200 shadow-sm z-10 sticky top-0">
        
        <div className="flex flex-col gap-4">
          
          {/* Fixing Type Filter */}
          <div className="flex gap-4 items-center">
            <div className="w-32 shrink-0">
              <span className="text-[11px] font-medium text-slate-800 bg-slate-100 border-l-2 border-[#C91D25] px-2.5 py-1 uppercase tracking-wider block">
                WPC Type
              </span>
            </div>
            <div className="flex gap-6 flex-wrap">
              <label className="flex items-center gap-2 cursor-pointer group">
                <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-colors ${storeFilter.readyToFix ? 'border-[#C91D25] bg-[#C91D25]' : 'border-slate-300 group-hover:border-[#C91D25]'}`}>
                  {storeFilter.readyToFix && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                </div>
                <input
                  type="radio"
                  name="fixingType"
                  checked={storeFilter.readyToFix}
                  onChange={() => setStoreFilter({ readyToFix: true, looseItems: false })}
                  className="hidden"
                />
                <span className="text-sm font-semibold text-slate-700">Ready to Fix</span>
              </label>
              
              <label className="flex items-center gap-2 cursor-pointer group">
                <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-colors ${storeFilter.looseItems ? 'border-[#C91D25] bg-[#C91D25]' : 'border-slate-300 group-hover:border-[#C91D25]'}`}>
                  {storeFilter.looseItems && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                </div>
                <input
                  type="radio"
                  name="fixingType"
                  checked={storeFilter.looseItems}
                  onChange={() => setStoreFilter({ readyToFix: false, looseItems: true })}
                  className="hidden"
                />
                <span className="text-sm font-semibold text-slate-700">Loose Items</span>
              </label>
            </div>
          </div>

          {/* Main Product Types */}
          <div className="flex gap-4 items-center">
            <div className="w-32 shrink-0">
              <span className="text-[11px] font-medium text-slate-800 bg-slate-100 border-l-2 border-[#C91D25] px-2.5 py-1 uppercase tracking-wider block">
                {storeFilter.readyToFix ? 'Ready To Fix Type' : 'Loose Items Type'}
              </span>
            </div>
            <div className="flex flex-wrap gap-4">
              {availableMainTypes.map(type => (
                <label key={type} className="flex items-center gap-2 cursor-pointer group">
                  <div className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${selectedMainTypes.includes(type) ? 'bg-[#C91D25] border-[#C91D25]' : 'bg-white border-slate-300 group-hover:border-[#C91D25]'}`}>
                    {selectedMainTypes.includes(type) && <Check size={12} className="text-white" strokeWidth={3} />}
                  </div>
                  <input
                    type="checkbox"
                    checked={selectedMainTypes.includes(type)}
                    onChange={() => toggleMainType(type)}
                    className="hidden"
                  />
                  <span className="text-sm font-semibold text-slate-700">{type.charAt(0).toUpperCase() + type.slice(1)}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Catalog Content Area */}
      <div className="flex-1 overflow-y-auto p-6">
        
        {loadingStore ? (
          <div className="flex items-center justify-center h-40">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : selectedMainTypes.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-slate-400 py-32 h-full">
            <div className="w-20 h-20 bg-white rounded-2xl border border-slate-200 flex items-center justify-center mb-6 shadow-sm">
              <svg className="w-10 h-10 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h7" /></svg>
            </div>
            <h3 className="text-lg font-bold text-slate-700 mb-2">No Product Type Selected</h3>
            <p className="font-medium text-sm text-slate-500 text-center max-w-sm">Please select one or more Main Types from the top to view the available catalogue.</p>
          </div>
        ) : catalogStructure.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-slate-400 py-32 h-full">
            <div className="w-20 h-20 bg-white rounded-2xl border border-slate-200 flex items-center justify-center mb-6 shadow-sm">
              <ShoppingCart className="w-10 h-10 text-slate-300" />
            </div>
            <h3 className="text-lg font-bold text-slate-700 mb-2">No Products Found</h3>
            <p className="font-medium text-sm text-slate-500 text-center max-w-sm">No products match the selected criteria or search query.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-8 pb-20">
            {catalogStructure.map(category => (
              <div key={category.categoryName} className="flex flex-col gap-6">
                
                {/* Category Qualities Selection - Compact */}
                {category.availableQualities.length > 0 && (
                  <div className="flex gap-4 items-center">
                    <div className="w-32 shrink-0">
                      <h3 className="text-[11px] font-medium text-slate-800 bg-slate-100 border-l-2 border-[#C91D25] px-2.5 py-1 uppercase tracking-wider block">{category.categoryName} Quality</h3>
                    </div>
                    <div className="flex flex-wrap gap-4">
                      {category.availableQualities.map(quality => (
                        <label key={quality} className="flex items-center gap-2 cursor-pointer group">
                          <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center transition-colors ${category.selectedQualities.includes(quality) ? 'bg-[#3b82f6] border-[#3b82f6]' : 'bg-white border-slate-300'}`}>
                            {category.selectedQualities.includes(quality) && <Check size={10} className="text-white" strokeWidth={3} />}
                          </div>
                          <input
                            type="checkbox"
                            checked={category.selectedQualities.includes(quality)}
                            onChange={() => toggleQuality(category.categoryName, quality)}
                            className="hidden"
                          />
                          <span className="text-xs text-slate-700">{quality}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Category SubField Selection - Compact */}
                {category.availableSubFields && category.availableSubFields.length > 0 && (
                  <div className="flex gap-4 items-center">
                    <div className="w-32 shrink-0">
                      <h3 className="text-[11px] font-medium text-slate-800 bg-slate-100 border-l-2 border-[#C91D25] px-2.5 py-1 uppercase tracking-wider block">{category.categoryName} {category.subFieldLabel}</h3>
                    </div>
                    <div className="flex flex-wrap gap-4">
                      {category.availableSubFields.map(sub => (
                        <label key={sub} className="flex items-center gap-2 cursor-pointer group">
                          <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center transition-colors ${category.selectedSubFields.includes(sub) ? 'bg-[#3b82f6] border-[#3b82f6]' : 'bg-white border-slate-300'}`}>
                            {category.selectedSubFields.includes(sub) && <Check size={10} className="text-white" strokeWidth={3} />}
                          </div>
                          <input
                            type="checkbox"
                            checked={category.selectedSubFields.includes(sub)}
                            onChange={() => toggleSubField(category.categoryName, sub)}
                            className="hidden"
                          />
                          <span className="text-xs text-slate-700">{sub}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Product Groups within Category */}
                <div className="flex flex-col gap-6 mt-2">
                  {category.groups.map(group => (
                    <div key={group.name} className="flex flex-col gap-3">
                      <h4 className="text-[11px] font-medium text-slate-800 bg-slate-100 border-l-2 border-[#C91D25] px-2.5 py-1 uppercase tracking-wider w-fit">{group.name}</h4>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-x-6 gap-y-5">
                        {group.items.map(stockItem => {
                          const item = stockItem.item || {};
                          const imgUrl = getImageUrl(item);
                          const sizeText = item.width && item.height ? `(${item.width} x ${item.height})` : '';
                          const stockQuantity = stockQuantities[stockItem._id] !== undefined ? stockQuantities[stockItem._id] : '';
                          
                          return (
                            <div key={stockItem._id} className="flex flex-col gap-1.5">
                              <span className="text-[11px] font-medium text-slate-700 uppercase leading-snug line-clamp-2">
                                {item.itemName} {sizeText}
                              </span>
                              
                              <div className="flex items-center gap-1.5">
                                <div className="w-10 h-10 bg-slate-50 border border-slate-200 flex items-center justify-center overflow-hidden shrink-0 rounded-sm">
                                  {imgUrl ? (
                                    <img src={imgUrl} alt={item.itemName} className="w-full h-full object-contain mix-blend-multiply p-0.5" />
                                  ) : (
                                    <ImageIcon className="w-3 h-3 text-slate-300" />
                                  )}
                                </div>
                                
                                <input 
                                  type="number"
                                  min="1"
                                  value={stockQuantity}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    setStockQuantities(prev => ({...prev, [stockItem._id]: val}));
                                  }}
                                  placeholder="0"
                                  disabled={isLocked}
                                  className="w-12 h-8 text-center text-[11px] border border-slate-200 rounded-sm focus:outline-none focus:border-blue-400 disabled:bg-slate-50"
                                />
                                
                                <button
                                  disabled={isLocked || !stockQuantity || parseInt(stockQuantity) <= 0}
                                  onClick={() => handleAddStockItem(stockItem, stockQuantity)}
                                  className="h-8 px-3 bg-blue-50 hover:bg-blue-100 text-blue-500 disabled:bg-slate-50 disabled:text-slate-300 text-[10px] uppercase rounded-sm transition-colors"
                                >
                                  Add
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
